import { memo, useMemo } from "react";

import classes from "./VerificationBox.module.css";
import { AttyTabColor, TransportationColor } from "../../../../lib/detailsTabsColors";
import { isStateOfPip } from "../../../../lib/script";
import { getMedPayPolicyLimitsLabel, getStatusOfInsurance } from "../../../../lib/labels";
import { liabilityFunctions } from "../../../../lib/liability";
import { medpayFunctions } from "../../../../lib/medpay";
import { umFunctions } from "../../../../lib/um";
import { majormedFunctions } from "../../../../lib/majormed";
import WorkQDeferredTooltip from "../../cells/WorkQDeferredTooltip";

function VerificationBox({ displayData, rowData, type, isPIP, lastCell, folderData }) {
    const caseState = rowData?.case?.state;
    const liability = rowData?.liability;
    const mpPip = rowData?.mp_pip;
    const um = rowData?.um;
    const majorMed = rowData?.major_med;
    const transportation = rowData?.case_transportation;
    const attorney = rowData?.attorney;
    const liabilities = rowData?.liabilities;
    const caseAdditional = rowData?.case_additional;
    const caseInfo = rowData?.case;
    const practices = rowData?.practices;
    const procodocsDocuments = rowData?.procodocs_documents;
    const attyActiveCase = useMemo(
        () => ({
            case: caseInfo,
            practices: practices ?? [],
            procodocs_documents: procodocsDocuments ?? [],
            attorneys: attorney ? [attorney] : [],
            case_attorney: attorney ?? rowData?.case_attorney,
        }),
        [caseInfo, practices, procodocsDocuments, attorney, rowData?.case_attorney],
    );
    const verificationCaseContext = useMemo(
        () => ({
            case: caseInfo,
            attorney,
            liabilities,
            case_additional: caseAdditional,
            liability,
            mp_pip: mpPip,
            um,
            major_med: majorMed,
            case_transportation: transportation,
        }),
        [caseInfo, attorney, liabilities, caseAdditional, liability, mpPip, um, majorMed, transportation],
    );

    const tabStatus = useMemo(() => {
        let next = {
            dot: false,
            dot_color: null,
            background: null,
            color: `var(--white)`,
            message: null,
            classColor: null,
        };

        if (type === "atty") {
            next = {
                ...AttyTabColor(attorney, liabilities, caseAdditional, attyActiveCase),
            };
        } else if (type === "liability") {
            next = {
                ...next,
                ...liabilityFunctions.liabilityVerification(liability),
            };
        } else if (type === "medpay") {
            next = {
                ...next,
                ...medpayFunctions.medpayVerification(mpPip, verificationCaseContext),
            };
        } else if (type === "um") {
            next = {
                ...next,
                ...umFunctions.umVerification(um, liabilities ?? liability),
            };
        } else if (type === "majormed") {
            next = {
                ...next,
                ...majormedFunctions.majormedVerification(majorMed, verificationCaseContext),
            };
        } else if (type === "transportation") {
            next = {
                ...TransportationColor(transportation, verificationCaseContext),
            };
        }

        return next;
    }, [type, attorney, liabilities, caseAdditional, liability, mpPip, um, majorMed, transportation, verificationCaseContext, attyActiveCase]);

    const itemDetails = useMemo(() => {
        const typeStatusLabel = getStatusOfInsurance(folderData, type);
        if (!isPIP) {
            return `${folderData?.carrier_name} / ${typeStatusLabel ?? "-"}`;
        }
        return (
            <div>
                <div>{folderData?.carrier_name}</div>
                {isStateOfPip(folderData?.insurance_state) && (
                    <div>PIP - {getStatusOfInsurance(folderData, "pip")}</div>
                )}
                <div>MP - {getStatusOfInsurance(folderData, "mp")}</div>
            </div>
        );
    }, [isPIP, folderData, type, caseState]);

    const modalMessage = useMemo(() => {
        return (
            <div className={classes.message_inner_wrapper}>
                <div className={classes.carrier_name}>{itemDetails}</div>
                {tabStatus.message && (
                    <div className={classes.message_inner} dangerouslySetInnerHTML={{ __html: tabStatus.message }} />
                )}
            </div>
        );
    }, [tabStatus.message, itemDetails]);

    const tabContent = (
        <div
            className={`${classes.tab} ${tabStatus.classColor ? tabStatus.classColor : ""}`}
            style={{ background: tabStatus.background, color: tabStatus.color }}
        >
            {isPIP ? (
                <>
                    <>{displayData ? displayData : "-"}</>
                    {isStateOfPip(folderData?.insurance_state ?? mpPip?.insurance_state) && mpPip?.pip_policy_limit && (
                        <> || {getMedPayPolicyLimitsLabel(mpPip?.pip_policy_limit)}</>
                    )}
                </>
            ) : (
                <>{displayData ? displayData : "-"}</>
            )}

            {tabStatus.dot && (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path
                        fill={tabStatus.dot_color ? tabStatus.dot_color : ""}
                        d="M12 2c5.523 0 10 4.477 10 10a10 10 0 0 1 -19.995 .324l-.005 -.324l.004 -.28c.148 -5.393 4.566 -9.72 9.996 -9.72zm.01 13l-.127 .007a1 1 0 0 0 0 1.986l.117 .007l.127 -.007a1 1 0 0 0 0 -1.986l-.117 -.007zm-.01 -8a1 1 0 0 0 -.993 .883l-.007 .117v4l.007 .117a1 1 0 0 0 1.986 0l.007 -.117v-4l-.007 -.117a1 1 0 0 0 -.993 -.883z"
                    />
                </svg>
            )}
        </div>
    );

    return (
        <div className={classes.container}>
            <WorkQDeferredTooltip mode={"v3"} message={modalMessage} wrapperClassName={classes.tooltip_wrapper}>
                {tabContent}
            </WorkQDeferredTooltip>
        </div>
    );
}

function verificationBoxPropsEqual(prevProps, nextProps) {
    if (prevProps.displayData !== nextProps.displayData) return false;
    if (prevProps.type !== nextProps.type) return false;
    if (prevProps.isPIP !== nextProps.isPIP) return false;
    if (prevProps.folderData !== nextProps.folderData) return false;
    if (prevProps.rowData?.case?.state !== nextProps.rowData?.case?.state) return false;
    if (prevProps.rowData?.mp_pip?.pip_policy_limit !== nextProps.rowData?.mp_pip?.pip_policy_limit) return false;

    const prevRow = prevProps.rowData;
    const nextRow = nextProps.rowData;
    switch (nextProps.type) {
        case "atty":
            return (
                prevRow?.attorney === nextRow?.attorney &&
                prevRow?.liabilities === nextRow?.liabilities &&
                prevRow?.case_additional === nextRow?.case_additional &&
                prevRow?.practices === nextRow?.practices &&
                prevRow?.procodocs_documents === nextRow?.procodocs_documents
            );
        case "liability":
            return prevRow?.liability === nextRow?.liability;
        case "medpay":
            return prevRow?.mp_pip === nextRow?.mp_pip;
        case "um":
            return prevRow?.um === nextRow?.um && prevRow?.liability === nextRow?.liability;
        case "majormed":
            return prevRow?.major_med === nextRow?.major_med;
        case "transportation":
            return prevRow?.case_transportation === nextRow?.case_transportation;
        default:
            return prevProps.rowData === nextProps.rowData;
    }
}

export default memo(VerificationBox, verificationBoxPropsEqual);
